import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertTriangle } from 'lucide-react';
import { client } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

/**
 * Adding one vendor by hand (PRD §F1).
 *
 * Lifted out of the rolodex page because the composer needs it too: a planner
 * who opens a category and finds the vendor they meant to add missing should
 * not have to leave a half-picked batch to go and add it. One dialog, so the
 * duplicate check and the required fields cannot drift between the two places
 * a vendor is born.
 */

export default function AddVendorDialog({
  open,
  onOpenChange,
  defaultCategory,
  defaultBusinessName,
  defaultEmail,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /** Pre-picked category, for the callers that already know it — opening this
   *  from a Florist batch is not a moment to make the planner say "Florist". */
  defaultCategory?: string;
  /** Prefill, for callers that already have the planner's typing — the
   *  recipient field's "Add …" row should not make them say it twice. */
  defaultBusinessName?: string;
  defaultEmail?: string;
  /** Fired with the created vendor, so a caller can select it immediately. */
  onCreated?: (vendor: { id: string }) => void;
}) {
  const queryClient = useQueryClient();

  // The dialog's own query rather than a prop: both callers would otherwise
  // pass the same list, and one of them would eventually pass a stale one.
  const { data: categories } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const res = await client.api.categories.$get();
      if (!res.ok) throw new Error('Failed to load categories');
      return (await res.json()) as { id: string; name: string }[];
    },
    enabled: open,
  });

  const blank = {
    businessName: defaultBusinessName ?? '',
    contactName: '',
    email: defaultEmail ?? '',
    phone: '',
    website: '',
    primaryCategory: defaultCategory ?? '',
    notes: '',
  };

  const [form, setForm] = useState(blank);

  // Reopening starts clean — including back on the caller's category, which a
  // planner who cleared it last time did not mean to clear forever.
  useEffect(() => {
    if (open) {
      setForm(blank);
      setDuplicates([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultCategory, defaultBusinessName, defaultEmail]);

  /**
   * PRD §F1: duplicate detection warns *before* a second record exists. Fired
   * on blur rather than per keystroke — a warning that flickers while you type
   * is noise, and this one is meant to make you stop and look.
   */
  const [duplicates, setDuplicates] = useState<{ id: string; businessName: string }[]>([]);

  const checkDuplicate = async () => {
    if (!form.email.trim() && !form.businessName.trim()) return;
    const res = await client.api.vendors['check-duplicate'].$get({
      query: { email: form.email.trim(), name: form.businessName.trim() },
    });
    if (!res.ok) return;
    const data = await res.json();
    setDuplicates(data.duplicates.map((d) => ({ id: d.id, businessName: d.businessName })));
  };

  const create = useMutation({
    mutationFn: async () => {
      const res = await client.api.vendors.$post({
        json: {
          businessName: form.businessName.trim(),
          contactName: form.contactName.trim() || null,
          phone: form.phone.trim() || null,
          website: form.website.trim() || null,
          primaryCategory: form.primaryCategory || null,
          notes: form.notes.trim() || null,
          secondaryCategories: [],
          emails: form.email.trim() ? [form.email.trim()] : [],
        },
      });
      if (!res.ok) {
        const err = (await res.json()) as { error?: string };
        throw new Error(err.error ?? 'Failed to create vendor');
      }
      return res.json();
    },
    onSuccess: (vendor) => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      onOpenChange(false);
      // Not a plain `setForm(blank)`: `blank.businessName`/`blank.email` come
      // from defaultBusinessName/defaultEmail, which are one-shot text the
      // planner typed once and just spent on creating this vendor, so they
      // reset to '' instead of falling through from `blank`. `primaryCategory`
      // is left to fall through, because defaultCategory is the opposite kind
      // of prop — a standing preference for whoever opened this dialog (a
      // Florist batch stays a Florist batch no matter how many vendors get
      // added to it), not text that gets "used up" by one create. DialogContent
      // stays mounted through its ~200ms close animation (`ui/dialog.tsx`), so
      // re-seeding businessName/email from `blank` here would show the
      // just-submitted name and email fading out instead of the form actually
      // clearing.
      setForm({ ...blank, businessName: '', email: '' });
      setDuplicates([]);
      // Ahead of the toast on purpose: both calls report the same event, and
      // if a caller's onCreated throws, the success toast should not fire
      // either — it would be telling the planner the hand-off worked when it
      // didn't. (This is not about timing: the whole callback runs
      // synchronously in one tick, and toast.success renders nothing of this
      // component, so the two calls would land in the same frame either way.)
      onCreated?.(vendor);
      toast.success('Vendor added');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add vendor</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="businessName">Business name *</Label>
            <Input
              id="businessName"
              autoFocus
              value={form.businessName}
              onBlur={checkDuplicate}
              onChange={(e) => setForm({ ...form, businessName: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="contactName">Contact name</Label>
              <Input
                id="contactName"
                value={form.contactName}
                onChange={(e) => setForm({ ...form, contactName: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={form.email}
                onBlur={checkDuplicate}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
          </div>

          {duplicates.length > 0 && (
            <p className="flex items-start gap-2 rounded-2xl bg-[var(--color-tertiary-container)] px-4 py-3 text-sm">
              <AlertTriangle className="mt-0.5 size-4 shrink-0" />
              <span>
                You already have{' '}
                {duplicates.map((d, i) => (
                  <span key={d.id}>
                    {i > 0 && ', '}
                    <Link to={`/vendors/${d.id}`} className="font-medium underline underline-offset-2">
                      {d.businessName}
                    </Link>
                  </span>
                ))}
                . Adding this creates a second record.
              </span>
            </p>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Select
                value={form.primaryCategory}
                onValueChange={(v) => setForm({ ...form, primaryCategory: v })}
              >
                <SelectTrigger id="category">
                  <SelectValue placeholder="Pick one" />
                </SelectTrigger>
                <SelectContent>
                  {(categories ?? []).map((c) => (
                    <SelectItem key={c.id} value={c.name}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="website">Website</Label>
            <Input
              id="website"
              placeholder="bloomfloral.com"
              value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              rows={3}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!form.businessName.trim() || create.isPending} onClick={() => create.mutate()}>
            {create.isPending ? 'Adding…' : 'Add vendor'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
